import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimatedNumber from "../../common/components/AnimatedNumber";
import { SPACING } from "../../common/constants/spacing";

const formatCurrency = (amount, currency) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(amount);
  } catch {
    return `${currency || "USD"} ${Number(amount).toFixed(2)}`;
  }
};

const PlatformTable = ({ sales }) => (
  <TableContainer sx={{ overflowX: "auto" }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Title
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Type
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="subtitle2" fontWeight="bold">
              Unit Price
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="subtitle2" fontWeight="bold">
              Qty
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="subtitle2" fontWeight="bold">
              Royalty
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Period
            </Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sales.map((sale, index) => (
          <TableRow key={index} hover>
            <TableCell>
              <Typography variant="bodySm">{sale.asset_title}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="bodySm">{sale.type}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="bodySm">
                {formatCurrency(sale.unit_price, sale.unit_price_currency)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="bodySm">{sale.quantity}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="bodySm" color="primary.main">
                {formatCurrency(sale.royalty_amount, sale.royalty_currency)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="bodySm" color="text.secondary">
                {sale.period_start} → {sale.period_end}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const PlatformSalesDetails = ({ analytics }) => {
  const { t } = useTranslation("analytics");
  const sales = analytics?.sales;

  if (!sales || sales.length === 0) return null;

  const hasLabels = sales.some((s) => s.source_label);
  if (!hasLabels) return null;

  // Group by source_label
  const groups = {};
  for (const sale of sales) {
    const key = sale.source_label || "__other__";
    if (!groups[key]) groups[key] = [];
    groups[key].push(sale);
  }

  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (a === "__other__") return 1;
    if (b === "__other__") return -1;
    return a.localeCompare(b);
  });

  return (
    <Box>
      <Typography
        variant="h3"
        fontWeight="bold"
        sx={{ mt: SPACING.lg, mb: SPACING.sm }}
      >
        {t("platformSalesDetails.title")}
      </Typography>

      {sortedKeys.map((key) => {
        const groupSales = groups[key];
        const totalRoyalty = groupSales.reduce(
          (sum, s) => sum + Number(s.royalty_amount),
          0,
        );

        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1,
              }}
            >
              <Chip
                label={key === "__other__" ? "Other" : key}
                color={key === "__other__" ? "default" : "primary"}
                variant="outlined"
              />
              <Typography variant="bodySm" color="text.secondary">
                {groupSales.length} record{groupSales.length !== 1 ? "s" : ""}
              </Typography>
              <Typography
                variant="bodySm"
                color="primary.main"
                fontWeight="medium"
              >
                <AnimatedNumber
                  section="tables"
                  value={totalRoyalty}
                  isCurrency
                />
              </Typography>
            </Box>
            <Card sx={{ p: 0 }} variant="outlined">
              <PlatformTable sales={groupSales} />
            </Card>
          </Box>
        );
      })}
    </Box>
  );
};
